import { useEffect, useState } from "react";
import api, { formatApiError } from "../../lib/api";
import { formatDate } from "../../lib/format";
import { useAuth } from "../../context/AuthContext";
import PageHeader from "../../components/PageHeader";
import { Users, Plus, Loader2, Trash2, ArrowUp } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Checkbox } from "../../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import { ValidationHint } from "../../components/ValidationHint";
import { validateCodiceFiscale } from "../../lib/validators";
import { toast } from "sonner";

export default function Clienti() {
  const { user } = useAuth();
  const canPermanent = user && user.role === "titolare";
  const [clients, setClients] = useState([]);
  const [trash, setTrash] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("active"); // active | trash
  const [selected, setSelected] = useState(new Set());
  const [selectedTrash, setSelectedTrash] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", codice_fiscale: "" });
  const [cfBlur, setCfBlur] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, t] = await Promise.all([
        api.get("/studio/clients"),
        api.get("/studio/clients/cestino/list"),
      ]);
      setClients(a.data);
      setTrash(t.data);
      setSelected(new Set());
      setSelectedTrash(new Set());
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/studio/clients", form);
      toast.success("Cliente creato");
      setOpen(false);
      setForm({ name: "", email: "", password: "", codice_fiscale: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleTrash = (id) => setSelectedTrash((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSel = clients.length > 0 && selected.size === clients.length;
  const allSelTrash = trash.length > 0 && selectedTrash.size === trash.length;

  const moveToTrash = async (ids) => {
    const list = ids || [...selected];
    if (!list.length) return;
    setBusy(true);
    try {
      await api.post("/studio/clients/cestino/move", { ids: list });
      toast.success(`${list.length} ${list.length === 1 ? "cliente spostato" : "clienti spostati"} nel cestino`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const restore = async (ids) => {
    const list = ids || [...selectedTrash];
    if (!list.length) return;
    setBusy(true);
    try {
      await api.post("/studio/clients/cestino/restore", { ids: list });
      toast.success(`${list.length} ${list.length === 1 ? "cliente ripristinato" : "clienti ripristinati"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const permanentDelete = async (ids) => {
    const list = ids || [...selectedTrash];
    if (!list.length) return;
    if (!window.confirm(`Eliminare definitivamente ${list.length} ${list.length === 1 ? "cliente" : "clienti"}? L'operazione è irreversibile.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/studio/clients/cestino/delete", { ids: list });
      toast.success(`${data.deleted} ${data.deleted === 1 ? "cliente eliminato" : "clienti eliminati"} definitivamente`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const tabCls = (active) => `px-4 py-2 text-sm font-medium rounded-md transition-colors ${active ? "bg-amber-500 text-[#0a0d14]" : "text-slate-300 hover:bg-slate-800/60"}`;

  return (
    <div data-testid="clienti-page">
      <PageHeader
        title="Anagrafica Clienti"
        subtitle="Crea gli account cliente: ognuno accederà solo alla propria area riservata personale."
        icon={Users}
        actions={
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="new-client-button" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                <Plus className="h-4 w-4 mr-1" /> Nuovo Cliente
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#121824] border-slate-700 text-slate-100">
              <DialogHeader>
                <DialogTitle className="font-head">Nuovo cliente</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4" data-testid="new-client-form">
                <div>
                  <Label className="text-slate-300">Nome e cognome</Label>
                  <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    data-testid="client-name-input" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Codice fiscale</Label>
                  <Input value={form.codice_fiscale} onChange={(e) => setForm({ ...form, codice_fiscale: e.target.value.toUpperCase() })} onBlur={() => setCfBlur(true)}
                    data-testid="client-cf-input" className="mt-1.5 bg-slate-900/60 border-slate-700 font-mono" />
                  <ValidationHint result={validateCodiceFiscale(form.codice_fiscale)} show={cfBlur} testId="client-cf" />
                </div>
                <div>
                  <Label className="text-slate-300">Email</Label>
                  <Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                    data-testid="client-email-input" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <div>
                  <Label className="text-slate-300">Password</Label>
                  <Input required type="password" minLength={6} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                    data-testid="client-password-input" className="mt-1.5 bg-slate-900/60 border-slate-700" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="submit-client-button"
                    className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea cliente"}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="flex items-center gap-2 mb-4" data-testid="clienti-tabs">
        <button type="button" onClick={() => setView("active")} data-testid="clienti-tab-active" className={tabCls(view === "active")}>
          Clienti attivi <span className="font-mono">({clients.length})</span>
        </button>
        <button type="button" onClick={() => setView("trash")} data-testid="clienti-tab-trash" className={tabCls(view === "trash")}>
          <Trash2 className="h-3.5 w-3.5 inline mr-1 -mt-0.5" /> Cestino <span className="font-mono">({trash.length})</span>
        </button>
      </div>

      {view === "active" ? (
        <div className="pgp-card overflow-x-auto pgp-fade-up">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : clients.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm" data-testid="clienti-empty">Nessun cliente registrato.</div>
          ) : (
            <table className="w-full text-sm" data-testid="clienti-table">
              <thead>
                <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                  <th className="px-5 py-3 w-10">
                    <Checkbox checked={allSel} onCheckedChange={(v) => setSelected(v ? new Set(clients.map((c) => c.id)) : new Set())} data-testid="clienti-select-all" />
                  </th>
                  <th className="px-5 py-3">Cliente</th>
                  <th className="px-5 py-3">Codice Fiscale</th>
                  <th className="px-5 py-3">Registrato</th>
                  <th className="px-5 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {clients.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`client-row-${c.id}`}>
                    <td className="px-5 py-3.5">
                      <Checkbox checked={selected.has(c.id)} onCheckedChange={() => toggle(c.id)} data-testid={`client-check-${c.id}`} />
                    </td>
                    <td className="px-5 py-3.5 font-medium text-slate-100">
                      {c.name}
                      <div className="text-[11px] text-slate-500 font-mono">{c.email}</div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-300 font-mono">{c.codice_fiscale || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-400 font-mono text-xs">{formatDate(c.created_at)}</td>
                    <td className="px-5 py-3.5 text-right">
                      <Button size="sm" onClick={() => moveToTrash([c.id])} disabled={busy} data-testid={`client-delete-${c.id}`}
                        className="h-8 bg-red-600 hover:bg-red-700 text-white font-semibold">
                        <Trash2 className="h-4 w-4 mr-1" /> Elimina
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="pgp-card overflow-x-auto pgp-fade-up">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-amber-500" /></div>
          ) : trash.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm" data-testid="clienti-trash-empty">Il cestino clienti è vuoto.</div>
          ) : (
            <table className="w-full text-sm" data-testid="clienti-trash-table">
              <thead>
                <tr className="text-left text-slate-500 text-xs uppercase tracking-wider font-mono border-b border-slate-800">
                  <th className="px-5 py-3 w-10">
                    <Checkbox checked={allSelTrash} onCheckedChange={(v) => setSelectedTrash(v ? new Set(trash.map((c) => c.id)) : new Set())} data-testid="clienti-trash-select-all" />
                  </th>
                  <th className="px-5 py-3">Cliente</th>
                  <th className="px-5 py-3">Codice Fiscale</th>
                  <th className="px-5 py-3">Spostato il</th>
                  <th className="px-5 py-3 text-right">Azioni</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800">
                {trash.map((c) => (
                  <tr key={c.id} className="hover:bg-slate-800/40 transition-colors" data-testid={`client-trash-row-${c.id}`}>
                    <td className="px-5 py-3.5">
                      <Checkbox checked={selectedTrash.has(c.id)} onCheckedChange={() => toggleTrash(c.id)} data-testid={`client-trash-check-${c.id}`} />
                    </td>
                    <td className="px-5 py-3.5 font-medium text-slate-100">
                      {c.name}
                      <div className="text-[11px] text-slate-500 font-mono">{c.email}</div>
                    </td>
                    <td className="px-5 py-3.5 text-slate-300 font-mono">{c.codice_fiscale || "—"}</td>
                    <td className="px-5 py-3.5 text-slate-400 font-mono text-xs">{formatDate(c.cestino_at)}</td>
                    <td className="px-5 py-3.5">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" onClick={() => restore([c.id])} disabled={busy} data-testid={`client-trash-restore-${c.id}`}
                          className="h-8 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                          <ArrowUp className="h-4 w-4 mr-1" /> Ripristina
                        </Button>
                        {canPermanent && (
                          <Button size="sm" onClick={() => permanentDelete([c.id])} disabled={busy} data-testid={`client-trash-delete-${c.id}`}
                            className="h-8 bg-red-600 hover:bg-red-700 text-white font-semibold">
                            <Trash2 className="h-4 w-4 mr-1" /> Elimina definitivamente
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {view === "active" && selected.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0d1220] border-t border-slate-700 px-6 py-4 flex items-center justify-between shadow-2xl" data-testid="clienti-action-bar">
          <span className="text-slate-200 font-medium" data-testid="clienti-selection-counter">{selected.size} clienti selezionati</span>
          <Button onClick={() => moveToTrash()} disabled={busy} data-testid="clienti-bulk-delete-btn" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
            <Trash2 className="h-4 w-4 mr-1.5" /> Elimina selezionati
          </Button>
        </div>
      )}

      {view === "trash" && selectedTrash.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-[#0d1220] border-t border-slate-700 px-6 py-4 flex items-center justify-between shadow-2xl" data-testid="clienti-trash-action-bar">
          <span className="text-slate-200 font-medium" data-testid="clienti-trash-selection-counter">{selectedTrash.size} clienti selezionati</span>
          <div className="flex gap-3">
            <Button onClick={() => restore()} disabled={busy} data-testid="clienti-trash-restore-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              <ArrowUp className="h-4 w-4 mr-1.5" /> Ripristina selezionati
            </Button>
            {canPermanent && (
              <Button onClick={() => permanentDelete()} disabled={busy} data-testid="clienti-trash-delete-btn" className="bg-red-600 hover:bg-red-700 text-white font-semibold">
                <Trash2 className="h-4 w-4 mr-1.5" /> Elimina definitivamente
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
